// online.{brand}/dev/mfa — DEV/UAT-only stakeholder demo. Four buttons, each
// binds a SealOne Test Authenticator with a deterministic behaviour and fires
// a challenge against the user. 1/2/3/4 keyboard shortcuts via inline hotkeys.
//
// Hard env gate: production returns a 404 page (defence in depth — the route
// guard at App.tsx also blocks. Belt and braces because the consequence of
// shipping this to PROD is too large.)

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { authClient } from "../lib/auth";
import { brandLabel } from "../lib/brand";
import { useHotkeys } from "../lib/hotkeys";
import type { SessionView } from "../lib/session";

type Behavior = "APPROVE" | "APPROVE_DELAYED" | "REJECT" | "REJECT_DELAYED";
type ChallengeState = "APPROVED" | "REJECTED" | "TIMEOUT" | "ERROR";

interface ChallengeResult {
  behavior: Behavior;
  state: ChallengeState;
  durationMs: number;
  message?: string;
}

interface ButtonSpec {
  behavior: Behavior;
  label: string;
  hint: string;
}

const BUTTONS: ButtonSpec[] = [
  { behavior: "APPROVE", label: "Approve", hint: "SealOne Test Authenticator → auto-approve, 0 s" },
  { behavior: "APPROVE_DELAYED", label: "Approve +6 s", hint: "SealOne Test Authenticator → auto-approve, 6 s delay" },
  { behavior: "REJECT", label: "Reject", hint: "SealOne Test Authenticator → auto-reject, 0 s" },
  { behavior: "REJECT_DELAYED", label: "Reject +6 s", hint: "SealOne Test Authenticator → auto-reject, 6 s delay" },
];

async function runChallenge(userId: string, behavior: Behavior): Promise<ChallengeResult> {
  // 1) Bind the SealOne Test Authenticator (idempotent).
  const enrol = await authClient.fetchWithCsrf(
    "/api/mf-auth-adapter/v1/mfa/dev/enrol-test-authenticator",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        userId,
        authenticator: behavior,
        label: `Demo · ${behavior}`,
      }),
    },
  );
  if (!enrol.ok) {
    throw new Error(`enrol_failed_${enrol.status}`);
  }
  // 2) Fire the LOGIN challenge.
  const challenge = await authClient.fetchWithCsrf(
    "/api/mf-auth-adapter/v1/mfa/challenge",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        userId,
        action: "LOGIN",
        content: "Demo: stakeholder click",
      }),
    },
  );
  if (!challenge.ok) {
    throw new Error(`challenge_failed_${challenge.status}`);
  }
  const body = (await challenge.json()) as Omit<ChallengeResult, "behavior">;
  return { behavior, ...body };
}

export interface DevMfaProps {
  session: SessionView;
}

// Exported separately so test can mount with MODE injected.
export function DevMfaPage({ session }: DevMfaProps) {
  const [last, setLast] = useState<ChallengeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (behavior: Behavior) => runChallenge(session.sub, behavior),
    onSuccess: (data) => {
      setError(null);
      setLast(data);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  useHotkeys(
    {
      "1": () => mutation.mutate("APPROVE"),
      "2": () => mutation.mutate("APPROVE_DELAYED"),
      "3": () => mutation.mutate("REJECT"),
      "4": () => mutation.mutate("REJECT_DELAYED"),
    },
    !mutation.isPending,
  );

  const stateClass = last
    ? last.state === "APPROVED"
      ? "devices-state-active"
      : last.state === "REJECTED"
        ? "devices-state-disabled"
        : "devices-state-locked"
    : "";

  return (
    <>
      <header className="hub-page-header">
        <h1>{brandLabel(session.brand)} 2FA demo surface</h1>
        <p>
          DEV/UAT only. Each button binds a SealOne Test Authenticator with a
          deterministic behaviour to your demo user, then fires{" "}
          <code>POST /api/mf-auth-adapter/v1/mfa/challenge</code> with{" "}
          <code>action=LOGIN</code>. Press <kbd>1</kbd>–<kbd>4</kbd> to fire
          without the mouse.
        </p>
      </header>
      <section className="hub-explainer">
        <h2>Pick a behaviour</h2>
        <div className="dev-mfa-grid">
          {BUTTONS.map((b, idx) => (
            <button
              key={b.behavior}
              type="button"
              className="hub-cta-button dev-mfa-button"
              onClick={() => mutation.mutate(b.behavior)}
              disabled={mutation.isPending}
              data-testid={`dev-mfa-${b.behavior.toLowerCase()}`}
            >
              <span className="dev-mfa-label">
                {idx + 1}. {b.label}
              </span>
              <span className="dev-mfa-hint">{b.hint}</span>
            </button>
          ))}
        </div>
      </section>
      <section className="hub-explainer">
        <h2>Last result</h2>
        {error ? (
          <p style={{ color: "#b13a3a" }}>Error: {error}</p>
        ) : null}
        {last ? (
          <div className="dev-mfa-result">
            <div>
              <strong>Behaviour:</strong> <code>{last.behavior}</code>
            </div>
            <div>
              <strong>State:</strong>{" "}
              <span className={`devices-state ${stateClass}`}>{last.state}</span>
            </div>
            <div>
              <strong>Duration:</strong> <code>{last.durationMs} ms</code>
            </div>
            {last.message ? (
              <div>
                <strong>Message:</strong> {last.message}
              </div>
            ) : null}
          </div>
        ) : (
          <p style={{ margin: 0 }}>No challenge fired yet — pick a button above.</p>
        )}
      </section>
      <main className="hub-cta-row">
        <Link to="/" className="hub-cta-button secondary">
          Back to dashboard
        </Link>
      </main>
    </>
  );
}

// Production guard: render a 404-shaped page if the env says we're in prod.
// We check via import.meta.env.MODE AND a hard-coded host fallback — belt and
// braces (prompt: "never let the surface ship to PROD even if env detection
// misfires"). The fallback blocks the REAL customer domains (morgenfund.com /
// morgenfund.de / dws.de) regardless of MODE. It deliberately does NOT block
// the PoC sandbox hosts (morfengund.com — note the misspelling — and orgbx.com),
// which ARE the dev/demo environment where this 2FA demo surface is meant to run.
function isProductionHost(hostname: string = window.location.hostname): boolean {
  return (
    hostname.endsWith(".morgenfund.com") ||
    hostname.endsWith(".morgenfund.de") ||
    hostname.endsWith(".dws.de")
  );
}

export interface DevMfaRouteOpts {
  // Test seam — overrides import.meta.env.MODE.
  modeOverride?: string;
  hostnameOverride?: string;
}

export function DevMfa({
  session,
  modeOverride,
  hostnameOverride,
}: DevMfaProps & DevMfaRouteOpts) {
  const mode = modeOverride ?? import.meta.env.MODE;
  const hostname = hostnameOverride ?? (typeof window !== "undefined" ? window.location.hostname : "");
  if (mode === "production" || isProductionHost(hostname)) {
    return (
      <main className="hub-page-header" data-testid="dev-mfa-not-found">
        <h1>404 — Not found</h1>
        <p>This surface is not available on this environment.</p>
      </main>
    );
  }
  return <DevMfaPage session={session} />;
}
